/**
 * Sync Service
 * 
 * Automatically synchronizes public user data from /users to:
 * - /publicProfiles/{userId}
 * - /leaderboards/global/{userId}
 * 
 * This service ensures that public-facing collections stay in sync
 * with the private user profile without requiring joins or secondary queries.
 * 
 * ARCHITECTURE:
 * - Client-side sync for now (acceptable for MVP)
 * - Structured to be easily moved to Cloud Functions later
 * - All writes are idempotent (setDoc with merge: true)
 * - Failures are logged but don't block user operations
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';
import { syncToPublicProfile, PublicProfile } from './publicProfileService';
import { syncToLeaderboard } from './leaderboardService';

/**
 * Sync public user data from /users to /publicProfiles and /leaderboards
 * 
 * This function:
 * 1. Reads current user data from /users/{userId}
 * 2. Syncs to /publicProfiles/{userId}
 * 3. Syncs to /leaderboards/global/{userId} (only if profile is public)
 * 
 * @param userId - User ID to sync
 * @param userData - Optional partial user data. If provided, uses this instead of reading from Firestore.
 *                   Useful when you already have the updated data and want to avoid an extra read.
 * @returns Promise that resolves when sync is complete (or fails silently)
 * 
 * @example
 * // Sync after profile update (with data already available)
 * await syncPublicUserData(userId, updatedUserData);
 * 
 * // Sync after XP/level change (read from Firestore)
 * await syncPublicUserData(userId);
 */
export const syncPublicUserData = async (
  userId: string,
  userData?: Partial<User>
): Promise<void> => {
  // CRITICAL: Don't throw - this is a non-critical background sync
  try {
    if (!userId || typeof userId !== 'string') {
      if (import.meta.env.DEV) {
        console.warn('[SYNC] Invalid userId provided:', userId);
      }
      return;
    }

    let dataToSync: Partial<User>;

    if (userData) {
      // Use provided data (avoids extra Firestore read)
      dataToSync = userData;
    } else {
      // Read from Firestore
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          if (import.meta.env.DEV) {
            console.warn('[SYNC] User document does not exist:', userId);
          }
          return;
        }
        
        dataToSync = userSnap.data() as User;
      } catch (readError: any) {
        // Log but don't throw - sync failure shouldn't block user operations
        if (import.meta.env.DEV) {
          console.error('[SYNC] Error reading user data:', {
            userId,
            code: readError?.code,
            message: readError?.message
          });
        }
        return;
      }
    }

    // Extract only public fields that need syncing
    const publicFields: Partial<User> = {
      name: dataToSync.name,
      avatar: dataToSync.avatar,
      photoURL: dataToSync.photoURL,
      level: dataToSync.level,
      xp: dataToSync.xp,
      title: dataToSync.title,
      badges: dataToSync.badges,
      username: dataToSync.username, // Include username for search/availability checks
      referralStats: dataToSync.referralStats, // Include referralStats for referralCode sync
      public: dataToSync.public,
    };

    // Step 1: Sync to publicProfiles
    // This will create/update the public profile with current data
    await syncToPublicProfile(userId, publicFields);

    // Step 2: Sync to leaderboard (only if profile is public)
    // Check isPublic flag from the data we already have
    const isPublic = publicFields.public !== false; // Default to true unless explicitly false
    
    if (isPublic) {
      // Create PublicProfile object from the data we have
      const publicProfile: PublicProfile = {
        id: userId,
        userId,
        name: publicFields.name || '',
        avatar: publicFields.avatar || publicFields.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`,
        level: publicFields.level || 1,
        xp: publicFields.xp || 0,
        title: publicFields.title,
        badges: publicFields.badges || [],
        username: publicFields.username, // Include username for search/availability
        referralCode: publicFields.referralStats?.referralCode, // Include referralCode for validation
        isPublic: true,
        updatedAt: new Date()
      };
      
      // Sync to leaderboard
      try {
        await syncToLeaderboard(userId, publicProfile);
      } catch (leaderboardError: any) {
        // Log but don't throw - leaderboard sync failure is non-critical
        if (import.meta.env.DEV) {
          console.error('[SYNC] Error syncing to leaderboard:', {
            userId,
            code: leaderboardError?.code,
            message: leaderboardError?.message
          });
        }
      }
    } else {
      // Profile is private - skip leaderboard sync
      if (import.meta.env.DEV) {
        console.log('[SYNC] Profile is private, skipping leaderboard sync:', userId);
      }
    }

    if (import.meta.env.DEV) {
      console.log('[SYNC] ✅ Successfully synced public data for user:', userId);
    }
  } catch (error: any) {
    // CRITICAL: Never throw - sync failures must not block user operations
    if (import.meta.env.DEV) {
      console.error('[SYNC] ❌ Error in syncPublicUserData:', {
        userId,
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
    }
  }
};

/**
 * Helper: Check if user data contains fields that require sync
 * 
 * @param userData - User data to check
 * @returns true if sync is needed
 */
export const shouldSync = (userData: Partial<User>): boolean => {
  const syncFields = ['name', 'avatar', 'photoURL', 'level', 'xp', 'title', 'badges', 'public'];
  return syncFields.some(field => field in userData);
};

/**
 * Cloud Functions Migration Note:
 * 
 * To move this to Cloud Functions:
 * 1. Create a Firestore trigger on /users/{userId} writes
 * 2. Extract changed fields using change.before.data() and change.after.data()
 * 3. Call syncPublicUserData(userId, change.after.data())
 * 4. Remove client-side calls to syncPublicUserData
 * 
 * Example Cloud Function structure:
 * 
 * export const syncUserData = functions.firestore
 *   .document('users/{userId}')
 *   .onWrite(async (change, context) => {
 *     const userId = context.params.userId;
 *     const afterData = change.after.data();
 *     
 *     if (!afterData) {
 *       // Document deleted - cleanup publicProfiles and leaderboards
 *       return;
 *     }
 *     
 *     await syncPublicUserData(userId, afterData);
 *   });
 */

